import { Avatar, AvatarProps, Theme } from '@mui/material';
import { LockIcon } from '@synergy/theme-next';
import { makeStyles } from '@mui/styles';

import { PoolAvatarIconButton } from './pool-avatar-icon-button';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        position: 'relative',
        width: 'fit-content',
        hight: 'fit-content',
    },
    // Anchored to the avatar rather than to a fixed offset: the 137/150 this
    // held was measured off the 200-pixel avatar of the contact masks, and put
    // the button outside every smaller one.
    avatarButton: {
        position: 'absolute',
        bottom: '4%',
        right: '4%',
    },
    // Red, not black: black reads as "no picture" and the lock disappears into
    // the row. A blocked person is a warning, so it takes the warning colour
    // the theme already defines (#2193).
    locked: {
        backgroundColor: theme.palette.error.main,
        '& > img': {
            opacity: 0.3,
        },
    },
    lockIcon: {
        position: 'absolute',
        width: '40%',
        height: '40%',
        top: '30%',
        left: '30%',
    },
    imgContain: {
        '& > img': {
            width: 'auto',
            height: 'auto',
            maxWidth: '100%',
            maxHeight: '100%',
        },
    },
    imgCover: {
        '& > img': {
            width: '100%',
            height: '100%',
        },
    },
}));

const cleanString = (value: string): string => {
    return value.replace(/[^0-9a-z]/gi, '');
};

export interface PoolAvatarProps extends AvatarProps {
    firstName: string;
    lastName?: string;
    disabled?: boolean;
    styleType?: 'contain' | 'cover'; // default: 'contain'
    locked?: boolean;
    /** What the lock says — passed in, because this component has no translations. */
    lockedLabel?: string;
    /** What the button over the picture does, in words — shown on hover. */
    addLabel?: string;
    removeLabel?: string;
    onUploadClick?: () => void;
    onDeleteClick?: () => void;
}

export const PoolAvatar = (props: PoolAvatarProps) => {
    const {
        locked,
        lockedLabel = 'Gesperrt',
        addLabel,
        removeLabel,
        firstName,
        lastName,
        disabled,
        onUploadClick,
        onDeleteClick,
        styleType,
        ...avatarProps
    } = props;
    const classes = useStyles();
    if (firstName.length === 0) {
        throw new Error('firstName must not be empty');
    }

    // Also with a picture: MUI shows the children only where the picture does
    // not load, and without them it fell back to the first letter of the alt.
    let initials: string;
    const cleanFirstName = cleanString(firstName);
    const cleanLastName = lastName && cleanString(lastName);

    if (cleanFirstName && cleanLastName) {
        initials = cleanFirstName.substring(0, 1) + cleanLastName.substring(0, 1);
    } else {
        initials = cleanFirstName.substring(0, Math.min(2, cleanFirstName.length));
    }

    const className = `${props.className} ${styleType === 'cover' ? classes.imgCover : classes.imgContain} ${
        locked ? classes.locked : ''
    }`;

    return (
        <div className={classes.container}>
            <Avatar {...avatarProps} alt={`${firstName} ${lastName ?? ' '}`} className={className}>
                {/*
                 * A sentence does not fit in a 96-pixel circle: while the record
                 * was unsaved, the masks put one here and it was cut off on both
                 * sides (#2212, R-21). The circle shows the initials either way;
                 * that the picture cannot be added yet is said by the greyed-out
                 * button and the line beneath it.
                 */}
                {locked ? (
                    <LockIcon htmlColor="white" className={classes.lockIcon} titleAccess={lockedLabel} />
                ) : (
                    initials
                )}
            </Avatar>
            {onUploadClick && onDeleteClick && (
                <PoolAvatarIconButton
                    onClick={!!avatarProps.src || !!avatarProps.srcSet ? onDeleteClick : onUploadClick}
                    disabled={disabled}
                    className={classes.avatarButton}
                    hasSrc={!!avatarProps.src || !!avatarProps.srcSet}
                    addLabel={addLabel}
                    removeLabel={removeLabel}
                />
            )}
        </div>
    );
};
